import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';

import { setLocale } from 'features/intl/intlSlice';
import { useUser } from 'selectors/userSelectors';

type LayoutContextValue = {
  ctrlPressed: boolean;
  altPressed: boolean;
  metaPressed: boolean;
};

const LayoutContext = createContext<LayoutContextValue | null>(null);

export function useLayoutContext(): LayoutContextValue {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error(
      'useLayoutContext must be used within a LayoutContextProvider',
    );
  }
  return context;
}

export function LayoutContextProvider(props: PropsWithChildren) {
  const { children } = props;
  const dispatch = useDispatch();
  const { locale } = useUser();
  const [ctrlPressed, setCtrlPressed] = useState(false);
  const [altPressed, setAltPressed] = useState(false);
  const [metaPressed, setMetaPressed] = useState(false);

  useEffect(() => {
    const updateModifierKeys = (event: KeyboardEvent) => {
      // Browser keyboard events expose the platform-neutral modifier flags.
      // ctrlKey maps to Control on macOS and Windows, altKey maps to Option on
      // macOS and Alt on Windows, and metaKey maps to Command on macOS and the
      // Windows key on Windows.
      setCtrlPressed(event.ctrlKey);
      setAltPressed(event.altKey);
      setMetaPressed(event.metaKey);

      // Keep the layout shortcut in the same place as the shared modifier state
      // so nested components do not each register their own window listener.
      // The locale toggle uses Ctrl+Cmd+E on macOS and Ctrl+Win+E on Windows.
      if (
        event.ctrlKey &&
        event.metaKey &&
        !event.shiftKey &&
        (event.key === 'e' || event.key === 'E')
      ) {
        dispatch(setLocale(locale === 'en' ? 'nl' : 'en'));
        event.preventDefault();
      }
    };

    const handleBlur = () => {
      setCtrlPressed(false);
      setAltPressed(false);
      setMetaPressed(false);
    };

    window.addEventListener('keydown', updateModifierKeys);
    window.addEventListener('keyup', updateModifierKeys);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', updateModifierKeys);
      window.removeEventListener('keyup', updateModifierKeys);
      window.removeEventListener('blur', handleBlur);
    };
  }, [dispatch, locale]);

  const value = useMemo(
    () => ({
      ctrlPressed,
      altPressed,
      metaPressed,
    }),
    [altPressed, ctrlPressed, metaPressed],
  );

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}
